import React, { PropsWithChildren, FC, useCallback, useEffect, useState } from "react";

import { Button, LoadingSpinner, URLInput, validateURL } from "@xapp/components";

const ANALYSIS_LOADING_MESSAGES: string[] = [
    "Getting started...", // 3 seconds
    "Retreiving website...", // 6
    "Nice website!",
    "Learning about your business...", // 9
    "Looking for a logo...", // 12
    "Determining color scheme...", // 15
    "Looking for structured data...", // 18
    "Gathering training data...", // 21
    "Evaluating website technology...", // 24
    "Getting close, thanks for your patience...", // 25
    "Double checking everything...",
    "Something might be hung up...", // 28
    "Apologies, we recommend you skip at this point.  We had trouble parsing the provided website.", //31
    "Skip or refresh"
];

export interface AnalyzeWebsiteStepProps {
    /**
     * Configurable welcome header
     */
    welcomeMessage?: string;
    /**
     * Is the website currently being analyzed
     */
    analyzing?: boolean;
    /**
     * Is the component disabled
     */
    disabled?: boolean;
    /**
     * Initial website value
     */
    website?: string;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * User requests to analyze a website
     * @param website
     */
    onAnalyze(website: string): void;
    /**
     * User requests to skip the analysis
     */
    onSkipAnalyze(website: string): void;
}

export const AnalyzeWebsiteStep: FC<PropsWithChildren<AnalyzeWebsiteStepProps>> = props => {

    const {
        welcomeMessage,
        analyzing,
        disabled,
        onAnalyze,
        style,
        className,
        onSkipAnalyze
    } = props;

    const [website, setWebsite] = useState<string>(props.website || "");
    const [valid, setValid] = useState<boolean>(validateURL(props.website).valid);
    const [loadingMessage, setLoadingMessage] = useState<string>();
    const [showSkipButton, setShowSkipButton] = useState<boolean>(false);
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

    useEffect(() => {
        if (analyzing && currentMessageIndex < ANALYSIS_LOADING_MESSAGES.length - 1) {
            const randomDelay = Math.floor(Math.random() * 3000) + 2000; // Get random number between 3000 and 5000
            const timeoutId = setTimeout(() => {
                setCurrentMessageIndex((prevIndex) => prevIndex + 1);
                setLoadingMessage(ANALYSIS_LOADING_MESSAGES[currentMessageIndex]);
            }, randomDelay);

            return () => clearTimeout(timeoutId); // Clear timeout if component unmounts before timeout is up
        } else if (!analyzing) {
            setLoadingMessage(ANALYSIS_LOADING_MESSAGES[0]);
        }
    }, [analyzing, currentMessageIndex]); // Rerun effect when analyzing or currentMessageIndex value changes

    useEffect(() => {
        if (analyzing) {
            const buttonTimeoutId = setTimeout(() => {
                setShowSkipButton(true);
            }, 10000); // Set timeout for showing the button

            return () => clearTimeout(buttonTimeoutId); // Clear timeout if component unmounts before timeout is up
        } else {
            setShowSkipButton(false); // Reset showButton state when not analyzing
        }
    }, [analyzing]); // Run effect when analyzing value changes

    const handleWebsiteChange = useCallback((value: string, valid: boolean) => {
        setWebsite(value);
        setValid(valid);
    }, []);

    const handleClick = useCallback(() => {
        onAnalyze(website);
        setShowSkipButton(false); // Reset the button display state
    }, [website, onAnalyze]);

    const handleNextClick = () => {
        // Do something when the button is clicked
        onSkipAnalyze(website);
    };

    return (
        <div style={{ ...style }} className={className}>
            {analyzing ? (
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <LoadingSpinner
                        textStyle={{ fontSize: "1em" }}
                        loadingText={loadingMessage}
                    />
                    {showSkipButton &&
                        <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <Button
                                rightIcon="arrow_forward"
                                label={`Skip`}
                                variation="primary"
                                onClick={handleNextClick}
                            />
                            <div style={{ margin: "20px" }}>
                                <small>You can skip however you will need to fill out things yourself.  We don't recommend this, it will be faster if you wait.</small>
                            </div>
                        </div>
                    }
                </div>
            ) : (
                <div>
                    <h3>{welcomeMessage || "Welcome!"}</h3>
                    {props.children}
                    <p>We are going to start building your custom AI and will use your website as training data.  </p>
                    <span />
                    <p>Please provide your business website below, we will then crawl it so we can better understand your business and accelerate the onboarding.</p>
                    <div style={{ padding: "20px 0px 50px 0px" }}>
                        <URLInput
                            variation="outlined"
                            disabled={disabled}
                            label={"Business Website"}
                            value={website}
                            onChange={handleWebsiteChange}
                            helperText={"Analysis can take up to a minute once it starts.  We are going to look at the content of your website to try to understand your business better."}
                        />
                    </div>
                    <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "right" }}>
                        <Button
                            rightIcon="arrow_forward"
                            label={`Next`}
                            variation="primary"
                            onClick={handleClick}
                            disabled={!website || !valid}
                        />
                    </div>
                </div >
            )}
        </div>
    );
}
